// path: src/App.js

import React, { Component } from "react";
import AppBar from "@material-ui/core/AppBar";
import "./App.css";
import Profile from "./Profile";
import characters from "./Characters.json";
import Row from "./Components/row/Row.js";
import BottomNav from "./Components/BottomBar";
import { auth } from "./firebase";
import Home from "./Home";
import Collection from "./Collection";
import Achievements from "./Achievements";
import Recap from "./Recap";

import {
  clearBaseGameCompleted,
  markBaseGameCompleted,
  setMaxUnlockedLevel,
} from "./utils/progression";

import { isUsernameAllowed } from "./utils/usernameBlocklist";
import { signOut } from "firebase/auth";

import {
  getUserHighScore,
  setUserHighScore,
  getUserCollection,
  setUserCollection,
  getUserFoilCollection,
  setUserFoilCollection,
  getUserProfile,
  setUserProfile,
  getUserHearts,
  setUserHearts,
} from "./firebaseUtils";

const APP_VERSION = "Alpha 2.1";
const ENABLE_ADMIN_PANEL = true;

const LS_USERNAME_KEY = "mmu_username_v1";
const LS_PROFILE_IMAGE_KEY = "mmu_profile_image_id_v1";
const LS_GAMES_PLAYED_KEY = "mmu_games_played_v1";
const LS_HEARTS_KEY = "mmu_hearts_v1";
const LS_HEART_LAST_REFILL_AT_KEY = "mmu_heart_last_refill_at_v1";
const LS_COLLECTION_KEY = "mmu_collection_unlocked_v1";
const LS_FOIL_COLLECTION_KEY = "mmu_collection_foil_unlocked_v1";

const FOIL_CHANCE = 0.01; // 1%
const GEM_APPEARANCE_CHANCE = 0.001; // 0.1%
const FOUNDER_APPEARANCE_CHANCE = 0.0001; // 0.01%
const MAX_HEARTS = 3;
const HEART_REFILL_MS = 60 * 60 * 1000;
const HEART_REFRESH_CHECK_MS = 60 * 1000;
const RUN_TIMER_TICK_MS = 1000;
const BLITZ_DURATION_MS = 2 * 60 * 1000;
const SHY_CORGIS_FLIP_LOCK_MS = 800;
const MAX_ENDLESS_LEVEL = 999;
const ENDLESS_CARDS_PER_LEVEL = 16;
const ENDLESS_POINTS_PER_LEVEL = 16;
const TWISTED_ROTATIONS = [0, 90, 180, 270];

const normalizeId = (id) => {
  const n = Number(id);
  return Number.isFinite(n) ? n : id;
};

const formatRunDuration = (ms = 0) => {
  const totalSeconds = Math.max(0, Math.floor(Number(ms) / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return `${minutes}:${String(seconds).padStart(2, "0")}`;
};

class App extends Component {
  highScoreSaveTimer = null;
  collectionSaveTimer = null;
  foilCollectionSaveTimer = null;
  heartSaveTimer = null;
  levelIntroTimer = null;
  heartRefreshTimer = null;
  runTimerInterval = null;
  shyCorgisFlipLockTimer = null;
  unsubscribeAuth = null;

  // Define levels
  levels = [
    { cards: 4, threshold: 4 },
    { cards: 4, threshold: 8 },
    { cards: 4, threshold: 12 },

    { cards: 9, threshold: 21 },
    { cards: 9, threshold: 30 },
    { cards: 9, threshold: 39 },

    { cards: 16, threshold: 55 },
    { cards: 16, threshold: 71 },
    { cards: 16, threshold: 87 },
    { cards: 16, threshold: 103 },
  ];

  getLevelConfig = (levelIndex) => {
    if (this.levels[levelIndex]) {
      return this.levels[levelIndex];
    }

    const finalMainLevel = this.levels[this.levels.length - 1];
    const endlessLevelsCompleted = levelIndex - (this.levels.length - 1);

    return {
      cards: ENDLESS_CARDS_PER_LEVEL,
      threshold:
        finalMainLevel.threshold +
        endlessLevelsCompleted * ENDLESS_POINTS_PER_LEVEL,
    };
  };

  isFinalMainLevelCompleteChoice = () => {
    return (
      this.state.currentLevel === this.levels.length - 1 &&
      this.state.pendingNextLevel === this.levels.length
    );
  };

  constructor(props) {
    super(props);
    this.state = {
      isAuthenticated: false,
      user: null,

      screen: "home",
      twistedMode: false,
      shyCorgisMode: false,
      shyCorgisFlipLocked: false,
      hardcoreMode: false,
      shyHiddenCardIds: [],

      showLevelOverlay: false,
      isBoardVisible: true,
      showLevelCompleteOverlay: false,
      showHeartContinueOverlay: false,
      pendingNextLevel: null,
      pendingWrongCardId: null,

      cards: this.applyFoilToCards(this.getNewCards(this.levels[0].cards)),
      score: 0,
      topScore: 0,
      clicked: Array(this.levels[0].cards).fill(false),
      currentLevel: 0,
      usedCards: [],
      hearts: MAX_HEARTS,
      heartLastRefillAt: Date.now(),

      collectionUnlocked: [],
      collectionFoilUnlocked: [],

      profileImageId: null,
      username: "",
      gamesPlayed: 0,

      runCorrectClicks: 0,
      runLevelsCompleted: 0,
      runFoilsFound: 0,
      runNewUnlocks: 0,
      runOutcome: "loss",
      runLevelReached: 1,
      runMode: "normal",
      runStartedAt: null,
      runElapsedMs: 0,
      runFinalElapsedMs: 0,
      runEndReason: "",

      adminUnlockAll: false,
    };
  }

  componentDidMount() {
    this.syncGameBodyClass();
    this.loadGuestCollection();
    this.loadGuestProfile();
    this.loadGuestHearts();

    this.heartRefreshTimer = setInterval(() => {
      this.refreshHearts();
    }, HEART_REFRESH_CHECK_MS);

    window.addEventListener("focus", this.handleHeartResume);
    window.addEventListener("pageshow", this.handleHeartResume);
    window.addEventListener("online", this.handleHeartResume);
    document.addEventListener("visibilitychange", this.handleVisibilityChange);

    this.unsubscribeAuth = auth.onAuthStateChanged(async (user) => {
      if (user) {
        const [
          savedTopScore,
          savedCollection,
          savedFoilCollection,
          savedProfile,
          savedHearts,
        ] = await Promise.all([
          getUserHighScore(user.uid),
          getUserCollection(user.uid),
          getUserFoilCollection(user.uid),
          getUserProfile(user.uid),
          getUserHearts(user.uid),
        ]);

        const nextUsername = this.getSafeUsername(
          savedProfile?.username ||
            this.state.username ||
            this.generateDefaultUsername()
        );

        const localHeartState = this.getRefilledHeartState(
          this.state.hearts,
          this.state.heartLastRefillAt
        );

        const accountHeartState = savedHearts
          ? this.getRefilledHeartState(
              savedHearts.hearts,
              savedHearts.heartLastRefillAt
            )
          : null;

        const nextHeartState = accountHeartState || localHeartState;

        this.setState(
          (prevState) => ({
            isAuthenticated: true,
            user,
            topScore: Math.max(prevState.topScore, savedTopScore),
            username: nextUsername,
            profileImageId:
              savedProfile?.profileImageId !== null &&
              savedProfile?.profileImageId !== undefined
                ? normalizeId(savedProfile.profileImageId)
                : prevState.profileImageId,
            gamesPlayed: Math.max(
              prevState.gamesPlayed || 0,
              savedProfile?.gamesPlayed || 0
            ),
            hearts: nextHeartState.hearts,
            heartLastRefillAt: nextHeartState.heartLastRefillAt,
            collectionUnlocked: Array.from(
              new Set([
                ...(prevState.collectionUnlocked || []),
                ...(savedCollection || []),
              ].map(normalizeId))
            ),
            collectionFoilUnlocked: Array.from(
              new Set([
                ...(prevState.collectionFoilUnlocked || []),
                ...(savedFoilCollection || []),
              ].map(normalizeId))
            ),
          }),
          this.saveHeartState
        );
      } else {
        this.setState({
          isAuthenticated: false,
          user: null,
        });
      }
    });
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.screen !== this.state.screen) {
      this.syncGameBodyClass();
    }

    if (prevState.profileImageId !== this.state.profileImageId) {
      this.saveGuestProfileImage();

      if (this.state.isAuthenticated && this.state.user?.uid) {
        setUserProfile(this.state.user.uid, {
          profileImageId: this.state.profileImageId,
        }).catch((e) => console.error("Failed to save profile image:", e));
      }
    }

    if (prevState.gamesPlayed !== this.state.gamesPlayed) {
      this.saveGuestGamesPlayed();

      if (this.state.isAuthenticated && this.state.user?.uid) {
        setUserProfile(this.state.user.uid, {
          gamesPlayed: this.state.gamesPlayed || 0,
        }).catch((e) => console.error("Failed to save games played:", e));
      }
    }

    if (prevState.username !== this.state.username) {
      this.saveGuestUsername();

      if (this.state.isAuthenticated && this.state.user?.uid) {
        setUserProfile(this.state.user.uid, {
          username: this.getSafeUsername(this.state.username),
        }).catch((e) => console.error("Failed to save username:", e));
      }
    }

    if (
      prevState.hearts !== this.state.hearts ||
      prevState.heartLastRefillAt !== this.state.heartLastRefillAt
    ) {
      this.saveHeartState();
    }

    if (prevState.currentLevel !== this.state.currentLevel) {
      if (this.levelIntroTimer) clearTimeout(this.levelIntroTimer);

      this.setState({ showLevelOverlay: true }, () => {
        this.levelIntroTimer = setTimeout(() => {
          this.setState({ showLevelOverlay: false });
        }, 800);
      });
    }

    if (
      this.state.isAuthenticated &&
      this.state.user?.uid &&
      prevState.topScore !== this.state.topScore
    ) {
      if (this.highScoreSaveTimer) clearTimeout(this.highScoreSaveTimer);

      this.highScoreSaveTimer = setTimeout(() => {
        setUserHighScore(this.state.user.uid, this.state.topScore).catch((e) =>
          console.error("Failed to save high score:", e)
        );
      }, 400);
    }

    if (prevState.collectionUnlocked !== this.state.collectionUnlocked) {
      this.saveGuestCollection();

      if (this.state.isAuthenticated && this.state.user?.uid) {
        if (this.collectionSaveTimer) clearTimeout(this.collectionSaveTimer);

        this.collectionSaveTimer = setTimeout(() => {
          setUserCollection(
            this.state.user.uid,
            this.state.collectionUnlocked
          ).catch((e) => console.error("Failed to save collection:", e));
        }, 500);
      }
    }

    if (prevState.collectionFoilUnlocked !== this.state.collectionFoilUnlocked) {
      this.saveGuestFoilCollection();

      if (this.state.isAuthenticated && this.state.user?.uid) {
        if (this.foilCollectionSaveTimer) {
          clearTimeout(this.foilCollectionSaveTimer);
        }

        this.foilCollectionSaveTimer = setTimeout(() => {
          setUserFoilCollection(
            this.state.user.uid,
            this.state.collectionFoilUnlocked
          ).catch((e) => console.error("Failed to save foil collection:", e));
        }, 500);
      }
    }
  }

  componentWillUnmount() {
    if (this.highScoreSaveTimer) clearTimeout(this.highScoreSaveTimer);
    if (this.collectionSaveTimer) clearTimeout(this.collectionSaveTimer);

    if (this.foilCollectionSaveTimer) {
      clearTimeout(this.foilCollectionSaveTimer);
    }

    if (this.heartSaveTimer) {
      clearTimeout(this.heartSaveTimer);
    }

    if (this.levelIntroTimer) clearTimeout(this.levelIntroTimer);
    if (this.heartRefreshTimer) clearInterval(this.heartRefreshTimer);

    if (this.runTimerInterval) {
      clearInterval(this.runTimerInterval);
    }

    window.removeEventListener("focus", this.handleHeartResume);
    window.removeEventListener("pageshow", this.handleHeartResume);
    window.removeEventListener("online", this.handleHeartResume);
    document.removeEventListener("visibilitychange", this.handleVisibilityChange);

    if (this.shyCorgisFlipLockTimer) {
      clearTimeout(this.shyCorgisFlipLockTimer);
    }

    if (this.unsubscribeAuth) {
      this.unsubscribeAuth();
    }

    document.body.classList.remove("gameBoardActive");
  }

  shuffleArray = (array) => {
    let shuffled = array.slice();
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  applyFoilToCards = (cards) => {
    return cards.map((card) => ({
      ...card,
      isFoil: Math.random() < FOIL_CHANCE,
    }));
  };

  getRandomTwistedRotation = () => {
    return TWISTED_ROTATIONS[
      Math.floor(Math.random() * TWISTED_ROTATIONS.length)
    ];
  };

  applyTwistedRotationToCards = (cards, twistedMode = this.state?.twistedMode) => {
    return cards.map((card) => ({
      ...card,
      twistedRotation: twistedMode ? this.getRandomTwistedRotation() : 0,
    }));
  };

  prepareCardsForBoard = (cards, twistedMode = this.state?.twistedMode) => {
    return this.applyTwistedRotationToCards(
      this.applyFoilToCards(cards),
      twistedMode
    );
  };

  getMaxShyHiddenCards = () => {
  return Math.floor((this.state.cards || []).length / 2);
  };

  clearShyCorgisFlipLockAfterDelay = () => {
    if (this.shyCorgisFlipLockTimer) {
      clearTimeout(this.shyCorgisFlipLockTimer);
    }

    this.shyCorgisFlipLockTimer = setTimeout(() => {
      this.setState({ shyCorgisFlipLocked: false });
      this.shyCorgisFlipLockTimer = null;
    }, SHY_CORGIS_FLIP_LOCK_MS);
  };

  requestShyCorgiHide = (cardId) => {
  if (!this.state.shyCorgisMode) {
    return false;
  }

  const normalizedCardId = String(cardId);
  const maxHidden = this.getMaxShyHiddenCards();

    if (maxHidden <= 0) {
      return false;
    }

    if (this.state.shyHiddenCardIds.includes(normalizedCardId)) {
      return true;
    }

    if (this.state.shyCorgisFlipLocked) {
      return false;
    }

    if (this.state.shyHiddenCardIds.length >= maxHidden) {
      return false;
    }

  this.setState((prevState) => {
    if (prevState.shyHiddenCardIds.includes(normalizedCardId)) {
      return null;
    }

    if (prevState.shyCorgisFlipLocked) {
      return null;
    }

    if (prevState.shyHiddenCardIds.length >= this.getMaxShyHiddenCards()) {
      return null;
    }

    return {
      shyCorgisFlipLocked: true,
      shyHiddenCardIds: [...prevState.shyHiddenCardIds, normalizedCardId],
    };
  }, this.clearShyCorgisFlipLockAfterDelay);

  return true;
  };

  releaseShyCorgiHide = (cardId) => {
    const normalizedCardId = String(cardId);

    this.setState((prevState) => {
      if (!prevState.shyHiddenCardIds.includes(normalizedCardId)) {
        return null;
      }

      return {
        shyHiddenCardIds: prevState.shyHiddenCardIds.filter(
          (id) => id !== normalizedCardId
        ),
      };
    });
  };

  shuffle = () => {
    const c = this.applyTwistedRotationToCards(
      [...this.state.cards],
      this.state.twistedMode
    );

    for (let i = c.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [c[i], c[j]] = [c[j], c[i]];
    }

    this.setState({ cards: c });
  };

  loadGuestProfile = () => {
    try {
      const rawProfileImageId = localStorage.getItem(LS_PROFILE_IMAGE_KEY);
      const rawGamesPlayed = localStorage.getItem(LS_GAMES_PLAYED_KEY);
      const rawUsername = localStorage.getItem(LS_USERNAME_KEY);

      const parsedProfileImageId = rawProfileImageId
        ? normalizeId(rawProfileImageId)
        : null;

      const parsedGamesPlayed = Number(rawGamesPlayed);

      const username = rawUsername
        ? this.getSafeUsername(rawUsername)
        : this.generateDefaultUsername();

      localStorage.setItem(LS_USERNAME_KEY, username);

      this.setState({
        profileImageId: parsedProfileImageId,
        username,
        gamesPlayed: Number.isFinite(parsedGamesPlayed) ? parsedGamesPlayed : 0,
      });
    } catch {
      // ignore
    }
  };

  saveGuestProfileImage = () => {
    try {
      if (this.state.profileImageId === null || this.state.profileImageId === undefined) {
        localStorage.removeItem(LS_PROFILE_IMAGE_KEY);
        return;
      }

      localStorage.setItem(LS_PROFILE_IMAGE_KEY, String(this.state.profileImageId));
    } catch {
      // ignore
    }
  };

  saveGuestGamesPlayed = () => {
    try {
      localStorage.setItem(LS_GAMES_PLAYED_KEY, String(this.state.gamesPlayed || 0));
    } catch {
      // ignore
    }
  };

  selectProfileImage = (id) => {
    this.setState({
      profileImageId: normalizeId(id),
    });
  };

  loadGuestCollection = () => {
    try {
      const raw = localStorage.getItem(LS_COLLECTION_KEY);
      const foilRaw = localStorage.getItem(LS_FOIL_COLLECTION_KEY);

      const parsed = raw ? JSON.parse(raw) : [];
      const foilParsed = foilRaw ? JSON.parse(foilRaw) : [];

      this.setState({
        collectionUnlocked: Array.isArray(parsed)
          ? Array.from(new Set(parsed.map(normalizeId)))
          : [],
        collectionFoilUnlocked: Array.isArray(foilParsed)
          ? Array.from(new Set(foilParsed.map(normalizeId)))
          : [],
      });
    } catch {
      // ignore
    }
  };

  saveGuestCollection = () => {
    try {
      localStorage.setItem(
        LS_COLLECTION_KEY,
        JSON.stringify(this.state.collectionUnlocked || [])
      );
    } catch {
      // ignore
    }
  };

  saveGuestFoilCollection = () => {
    try {
      localStorage.setItem(
        LS_FOIL_COLLECTION_KEY,
        JSON.stringify(this.state.collectionFoilUnlocked || [])
      );
    } catch {
      // ignore
    }
  };

  findId = (id) => {
    return this.state.clicked.includes(id);
  };

  returnFirstNull = () => {
    return this.state.clicked.indexOf(false);
  };

  insertId = (id, index) => {
    this.setState({
      clicked: this.state.clicked.map((elem, i) => (i === index ? id : elem)),
    });
  };

  getCardAppearanceWeight = (card, standardWeight) => {
    if (card.rarity === "gem") {
      return GEM_APPEARANCE_CHANCE;
    }

    if (card.rarity === "founder") {
      return FOUNDER_APPEARANCE_CHANCE;
    }

    return standardWeight;
  };

  getNewCards = (num) => {
    const gemCards = characters.filter((card) => card.rarity === "gem");
    const founderCards = characters.filter((card) => card.rarity === "founder");
    const standardCards = characters.filter(
      (card) => card.rarity !== "gem" && card.rarity !== "founder"
    );

    const specialTotalChance =
      gemCards.length * GEM_APPEARANCE_CHANCE +
      founderCards.length * FOUNDER_APPEARANCE_CHANCE;

    const standardTotalChance = Math.max(0, 1 - specialTotalChance);

    const standardWeight =
      standardCards.length > 0 ? standardTotalChance / standardCards.length : 0;

    const weightedPool = characters
      .map((card) => ({
        card,
        weight: this.getCardAppearanceWeight(card, standardWeight),
      }))
      .filter((item) => item.weight > 0);

    const selectedCards = [];

    while (selectedCards.length < num && weightedPool.length > 0) {
      const totalWeight = weightedPool.reduce(
        (sum, item) => sum + item.weight,
        0
      );

      let roll = Math.random() * totalWeight;
      let selectedIndex = 0;

      for (let i = 0; i < weightedPool.length; i += 1) {
        roll -= weightedPool[i].weight;

        if (roll <= 0) {
          selectedIndex = i;
          break;
        }
      }

      const selectedItems = weightedPool.splice(selectedIndex, 1);
      selectedCards.push(selectedItems[0].card);
    }

    return selectedCards;
  };

  unlockCardIfNeeded = (rawId, isFoil = false) => {
    const id = normalizeId(rawId);

    this.setState((prev) => {
      const regular = prev.collectionUnlocked || [];
      const foil = prev.collectionFoilUnlocked || [];

      const nextState = {};

      if (!regular.includes(id)) {
        nextState.collectionUnlocked = [...regular, id];
        nextState.runNewUnlocks = (prev.runNewUnlocks || 0) + 1;
      }

      if (isFoil && !foil.includes(id)) {
        nextState.collectionFoilUnlocked = [...foil, id];
      }

      return Object.keys(nextState).length ? nextState : null;
    });
  };

  handleHeartResume = () => {
    this.refreshHearts();
    this.saveHeartState();
  };

  handleVisibilityChange = () => {
    if (document.visibilityState === "visible") {
      this.refreshHearts();
      this.saveHeartState();
    }
  };

  saveUserHearts = () => {
    if (!this.state.isAuthenticated || !this.state.user?.uid) {
      return;
    }

    if (this.heartSaveTimer) {
      clearTimeout(this.heartSaveTimer);
    }

    this.heartSaveTimer = setTimeout(() => {
      setUserHearts(this.state.user.uid, {
        hearts: this.state.hearts,
        heartLastRefillAt: this.state.heartLastRefillAt,
      }).catch((e) => console.error("Failed to save hearts:", e));
    }, 500);
  };

  saveHeartState = () => {
    this.saveGuestHearts();
    this.saveUserHearts();
  };

  getRefilledHeartState = (
    hearts = MAX_HEARTS,
    heartLastRefillAt = Date.now(),
    now = Date.now()
  ) => {
    const safeHearts = Math.max(0, Math.min(MAX_HEARTS, Number(hearts) || 0));
    const safeLastRefillAt = Number.isFinite(Number(heartLastRefillAt))
      ? Number(heartLastRefillAt)
      : now;

    if (safeHearts >= MAX_HEARTS) {
      return {
        hearts: MAX_HEARTS,
        heartLastRefillAt: now,
      };
    }

    const elapsed = Math.max(0, now - safeLastRefillAt);
    const refilled = Math.floor(elapsed / HEART_REFILL_MS);

    if (refilled <= 0) {
      return {
        hearts: safeHearts,
        heartLastRefillAt: safeLastRefillAt,
      };
    }

    const nextHearts = Math.min(MAX_HEARTS, safeHearts + refilled);

    return {
      hearts: nextHearts,
      heartLastRefillAt:
        nextHearts >= MAX_HEARTS
          ? now
          : safeLastRefillAt + refilled * HEART_REFILL_MS,
    };
  };

  loadGuestHearts = () => {
    try {
      const savedHearts = Number(localStorage.getItem(LS_HEARTS_KEY));
      const savedLastRefillAt = Number(
        localStorage.getItem(LS_HEART_LAST_REFILL_AT_KEY)
      );

      const nextHeartState = this.getRefilledHeartState(
        Number.isFinite(savedHearts) ? savedHearts : MAX_HEARTS,
        Number.isFinite(savedLastRefillAt) ? savedLastRefillAt : Date.now()
      );

      this.setState(nextHeartState, this.saveHeartState);
    } catch {
      // ignore localStorage failures
    }
  };

  saveGuestHearts = () => {
    try {
      localStorage.setItem(LS_HEARTS_KEY, String(this.state.hearts));
      localStorage.setItem(
        LS_HEART_LAST_REFILL_AT_KEY,
        String(this.state.heartLastRefillAt)
      );
    } catch {
      // ignore localStorage failures
    }
  };

  refreshHearts = () => {
    this.setState((prevState) => {
      const nextHeartState = this.getRefilledHeartState(
        prevState.hearts,
        prevState.heartLastRefillAt
      );

      if (
        nextHeartState.hearts === prevState.hearts &&
        nextHeartState.heartLastRefillAt === prevState.heartLastRefillAt
      ) {
        return null;
      }

      return nextHeartState;
    }, this.saveHeartState);
  };

  getNextHeartMinutesText = () => {
    if (this.state.hearts >= MAX_HEARTS) {
      return "Full";
    }

    const msRemaining = Math.max(
      0,
      HEART_REFILL_MS - (Date.now() - this.state.heartLastRefillAt)
    );
    const minutes = Math.max(1, Math.ceil(msRemaining / 60000));

    return `${minutes}m`;
  };

  endRunAsLoss = () => {
    this.stopRunTimer();

    this.setState({
      ...this.getFinishedRunTimingState(),
      screen: "recap",
      runOutcome: "loss",
      runEndReason: "wrong-card",
      runLevelReached: this.state.currentLevel + 1,
      showLevelCompleteOverlay: false,
      showHeartContinueOverlay: false,
      pendingWrongCardId: null,
      pendingNextLevel: null,
    });
  };

  handleWrongCard = (id) => {
    const nextHeartState = this.getRefilledHeartState(
      this.state.hearts,
      this.state.heartLastRefillAt
    );

    if (this.state.hardcoreMode || nextHeartState.hearts <= 0) {
      this.setState(nextHeartState, () => {
        this.saveHeartState();
        this.endRunAsLoss();
      });
      return;
    }

    this.setState(
      {
        ...nextHeartState,
        showHeartContinueOverlay: true,
        pendingWrongCardId: id,
        showLevelCompleteOverlay: false,
        pendingNextLevel: null,
      },
      this.saveHeartState
    );
  };

  spendHeartToContinue = () => {
    this.setState((prevState) => {
      const nextHeartState = this.getRefilledHeartState(
        prevState.hearts,
        prevState.heartLastRefillAt
      );

      if (nextHeartState.hearts <= 0) {
        return {
          ...nextHeartState,
          showHeartContinueOverlay: false,
          pendingWrongCardId: null,
          screen: "recap",
          runOutcome: "loss",
          runLevelReached: prevState.currentLevel + 1,
        };
      }

      const spentFromFull = nextHeartState.hearts >= MAX_HEARTS;

      return {
        hearts: nextHeartState.hearts - 1,
        heartLastRefillAt: spentFromFull
          ? Date.now()
          : nextHeartState.heartLastRefillAt,
        showHeartContinueOverlay: false,
        pendingWrongCardId: null,
      };
    }, this.saveHeartState);
  };

  declineHeartContinue = () => {
    this.endRunAsLoss();
  };

  handleClick = (event) => {
    const id = event.target.id;
    const isFoil = event.currentTarget.dataset.foil === "true";

    if (this.state.runMode === "blitz" && this.getBlitzRemainingMs() <= 0) {
      this.endRunFromBlitzTimeout();
      return;
    }

    if (this.findId(id)) {
      this.handleWrongCard(id);
      return;
    }

    this.unlockCardIfNeeded(id, isFoil);

    this.setState((prev) => ({
      runCorrectClicks: (prev.runCorrectClicks || 0) + 1,
      runFoilsFound: (prev.runFoilsFound || 0) + (isFoil ? 1 : 0),
    }));

    this.insertId(id, this.returnFirstNull());

    const newScore = this.state.score + 1;
    let newTopScore = this.state.topScore;

    if (newScore > this.state.topScore) {
      newTopScore = newScore;
    }

    this.setState({
      score: newScore,
      topScore: newTopScore,
    });

    this.shuffle();

    const currentLevelConfig = this.getLevelConfig(this.state.currentLevel);

    if (newScore >= currentLevelConfig.threshold) {
      const nextLevel = this.state.currentLevel + 1;

      this.setState({
        showLevelCompleteOverlay: true,
        pendingNextLevel: nextLevel,
        runLevelsCompleted: (this.state.runLevelsCompleted || 0) + 1,
      });
    }
  };

  finishRun = () => {
    markBaseGameCompleted();
    this.stopRunTimer();

    this.setState({
      ...this.getFinishedRunTimingState(),
      screen: "recap",
      runOutcome: "win",
      runEndReason: "completed-main-run",
      runLevelReached: this.state.currentLevel + 1,
      showLevelCompleteOverlay: false,
      pendingNextLevel: null,
    });
  };

  startNextLevel = () => {
    const nextLevel = this.state.pendingNextLevel;
    if (nextLevel == null) return;

    this.setState({
      showLevelCompleteOverlay: false,
      showLevelOverlay: false,
    });

    if (nextLevel >= MAX_ENDLESS_LEVEL) {
      this.stopRunTimer();

      this.setState({
        ...this.getFinishedRunTimingState(),
        screen: "recap",
        runOutcome: "win",
        runEndReason: "max-level",
        runLevelReached: MAX_ENDLESS_LEVEL,
        showLevelCompleteOverlay: false,
        showLevelOverlay: false,
        pendingNextLevel: null,
      });
      return;
    }

    if (this.isFinalMainLevelCompleteChoice()) {
      markBaseGameCompleted();
    }

    const nextLevelConfig = this.getLevelConfig(nextLevel);
    const fetchedNewCards = this.getNewCards(nextLevelConfig.cards);

    this.setState(
      {
        showLevelCompleteOverlay: false,
        showLevelOverlay: false,
        pendingNextLevel: null,
        currentLevel: nextLevel,
        shyCorgisFlipLocked: false,
        shyHiddenCardIds: [],
        cards: this.prepareCardsForBoard(
          this.shuffleArray(fetchedNewCards).slice(0, nextLevelConfig.cards),
          this.state.twistedMode
        ),
        clicked: Array(nextLevelConfig.cards).fill(false),
        isBoardVisible: true,
      },
      () => {
        setMaxUnlockedLevel(Math.min(nextLevel + 1, this.levels.length));
      }
    );
  };

  resetGameToStart = () => {
    this.stopRunTimer();

    this.setState({
      showLevelOverlay: false,
      isBoardVisible: true,
      showLevelCompleteOverlay: false,
      showHeartContinueOverlay: false,
      pendingNextLevel: null,
      pendingWrongCardId: null,

      cards: this.prepareCardsForBoard(this.getNewCards(this.levels[0].cards), false),
      twistedMode: false,
      shyCorgisMode: false,
      hardcoreMode: false,
      shyCorgisFlipLocked: false,
      shyHiddenCardIds: [],
      score: 0,
      topScore: this.state.topScore,
      clicked: Array(this.levels[0].cards).fill(false),
      currentLevel: 0,
      usedCards: [],

      runMode: "normal",
      runStartedAt: null,
      runElapsedMs: 0,
      runFinalElapsedMs: 0,
      runEndReason: "",
    });
  };

  goHome = () => {
    this.resetGameToStart();
    this.setState({ screen: "home" });
  };

  goCollection = () => {
    this.setState({ screen: "collection" });
  };

  goAchievements = () => {
    this.setState({ screen: "achievements" });
  };

  goProfile = () => {
    this.setState({ screen: "profile" });
  };

  getCurrentRunElapsedMs = () => {
    if (!this.state.runStartedAt) {
      return this.state.runFinalElapsedMs || this.state.runElapsedMs || 0;
    }

    return Math.max(0, Date.now() - this.state.runStartedAt);
  };

  getBlitzRemainingMs = () => {
    return Math.max(0, BLITZ_DURATION_MS - this.getCurrentRunElapsedMs());
  };

  getFinishedRunTimingState = () => {
    const elapsedMs = this.getCurrentRunElapsedMs();

    return {
      runElapsedMs: elapsedMs,
      runFinalElapsedMs: elapsedMs,
    };
  };

  startRunTimer = () => {
    if (this.runTimerInterval) {
      clearInterval(this.runTimerInterval);
    }

    this.runTimerInterval = setInterval(this.tickRunTimer, RUN_TIMER_TICK_MS);
    this.tickRunTimer();
  };

  stopRunTimer = () => {
    if (this.runTimerInterval) {
      clearInterval(this.runTimerInterval);
      this.runTimerInterval = null;
    }
  };

  tickRunTimer = () => {
    if (this.state.screen !== "game" || !this.state.runStartedAt) {
      return;
    }

    const elapsedMs = this.getCurrentRunElapsedMs();

    if (this.state.runMode === "blitz" && elapsedMs >= BLITZ_DURATION_MS) {
      this.endRunFromBlitzTimeout();
      return;
    }

    this.setState({
      runElapsedMs: elapsedMs,
    });
  };

  endRunFromBlitzTimeout = () => {
    if (this.state.screen !== "game") {
      return;
    }

    this.stopRunTimer();

    this.setState({
      ...this.getFinishedRunTimingState(),
      screen: "recap",
      runOutcome: "loss",
      runEndReason: "blitz-timeout",
      runLevelReached: this.state.currentLevel + 1,
      showLevelCompleteOverlay: false,
      showHeartContinueOverlay: false,
      pendingWrongCardId: null,
      pendingNextLevel: null,
    });
  };

  goPlay = ({
      startLevel = 1,
      startScore = 0,
      twistedMode = false,
      shyCorgisMode = false,
      hardcoreMode = false,
      blitzMode = false,
  } = {}) => {
    this.stopRunTimer();

    const effectiveStartLevel = blitzMode ? 1 : startLevel;
    const effectiveStartScore = blitzMode ? 0 : startScore;
    const effectiveHardcoreMode = blitzMode ? true : hardcoreMode;

    const startIndex = Math.max(
      0,
      Math.min(this.levels.length - 1, effectiveStartLevel - 1)
    );
    const cardsCount = this.levels[startIndex].cards;

    const startingCards = this.prepareCardsForBoard(
      this.getNewCards(cardsCount),
      twistedMode
    );

    this.setState(
      {
        showLevelOverlay: false,
        isBoardVisible: true,
        showLevelCompleteOverlay: false,
        showHeartContinueOverlay: false,
        pendingNextLevel: null,
        pendingWrongCardId: null,

        screen: "game",
        twistedMode,
        shyCorgisMode,
        shyCorgisFlipLocked: false,
        hardcoreMode: effectiveHardcoreMode,
        shyHiddenCardIds: [],
        gamesPlayed: (this.state.gamesPlayed || 0) + 1,

        runCorrectClicks: 0,
        runLevelsCompleted: 0,
        runFoilsFound: 0,
        runNewUnlocks: 0,
        runOutcome: "loss",
        runLevelReached: startIndex + 1,
        runMode: blitzMode ? "blitz" : "normal",
        runStartedAt: Date.now(),
        runElapsedMs: 0,
        runFinalElapsedMs: 0,
        runEndReason: "",

        cards: startingCards,
        score: effectiveStartScore,
        topScore: this.state.topScore,
        clicked: Array(cardsCount).fill(false),
        currentLevel: startIndex,
        usedCards: [],
      },
      () => {
        setMaxUnlockedLevel(startIndex + 1);
        this.startRunTimer();
      }
    );
  };

  adminGoToLevel = (levelNumber) => {
    const safeLevel = Math.max(1, Math.min(this.levels.length, levelNumber));

    this.goPlay({
      startLevel: safeLevel,
      startScore: this.levels[safeLevel - 2]?.threshold || 0,
    });
  };

  adminRefreshBoard = () => {
    const currentLevelConfig = this.getLevelConfig(this.state.currentLevel);
    const refreshedCards = this.prepareCardsForBoard(
      this.getNewCards(currentLevelConfig.cards),
      this.state.twistedMode
    );

    this.setState({
      cards: refreshedCards,
      clicked: Array(currentLevelConfig.cards).fill(false),
      showLevelCompleteOverlay: false,
      pendingNextLevel: null,
      isBoardVisible: true,
    });
  };

  adminToggleUnlockAll = (event) => {
    const enabled = event.target.checked;

    if (enabled) {
      const allIds = characters.map((c) => normalizeId(c.id));

      setMaxUnlockedLevel(this.levels.length);
      markBaseGameCompleted();

      this.setState({
        adminUnlockAll: true,
        collectionUnlocked: allIds,
        collectionFoilUnlocked: allIds,
      });

      return;
    }

    this.setState({ adminUnlockAll: false });
  };

  adminResetAllContent = () => {
      const emptyIds = [];

      try {
          localStorage.removeItem(LS_COLLECTION_KEY);
          localStorage.removeItem(LS_FOIL_COLLECTION_KEY);
          localStorage.setItem("mmu_max_level", "1");
          clearBaseGameCompleted();
      } catch {
          // ignore
      }

      this.setState({
          adminUnlockAll: false,
          collectionUnlocked: emptyIds,
          collectionFoilUnlocked: emptyIds,
          topScore: 0,
      });

      if (this.state.isAuthenticated && this.state.user?.uid) {
          setUserCollection(this.state.user.uid, emptyIds).catch((e) =>
              console.error("Failed to reset collection:", e)
          );

          setUserFoilCollection(this.state.user.uid, emptyIds).catch((e) =>
              console.error("Failed to reset foil collection:", e)
          );

          setUserHighScore(this.state.user.uid, 0).catch((e) =>
              console.error("Failed to reset high score:", e)
          );
      }
  };

  handleLogout = async () => {
    try {
      await signOut(auth);
    } finally {
      this.goHome();
      this.setState({ isAuthenticated: false });
    }
  };

  renderAdminPanel = () => {
    if (!ENABLE_ADMIN_PANEL) return null;

    return (
      <div className="adminPanel">
        <div className="adminPanel__title">Admin Panel</div>

        <div className="adminPanel__row">
          <button
            className="adminPanel__button"
            onClick={() => this.adminGoToLevel(this.state.currentLevel)}
            disabled={this.state.currentLevel <= 0}
          >
            Previous Level
          </button>

          <button
            className="adminPanel__button"
            onClick={() => this.adminGoToLevel(this.state.currentLevel + 2)}
            disabled={this.state.currentLevel >= this.levels.length - 1}
          >
            Next Level
          </button>
        </div>

        <div className="adminPanel__row">
          <button
            className="adminPanel__button"
            onClick={this.adminRefreshBoard}
            disabled={this.state.screen !== "game"}
          >
            Refresh Board
          </button>
        </div>

        <div className="adminPanel__row">
          <label className="adminPanel__label">
            <input
              type="checkbox"
              checked={this.state.adminUnlockAll}
              onChange={this.adminToggleUnlockAll}
            />
            Unlock all features/cards
          </label>
        </div>

        <div className="adminPanel__row">
          <button
            className="adminPanel__button adminPanel__button--danger"
            onClick={this.adminResetAllContent}
          >
            Reset / Lock All Content
          </button>
        </div>

        <div className="adminPanel__meta">
          Current Level: {this.state.currentLevel + 1}
        </div>
      </div>
    );
  };

  // path: src/App.js

  syncGameBodyClass = () => {
    document.body.classList.toggle(
      "gameBoardActive",
      this.state.screen === "game"
    );
  };

  generateDefaultUsername = () => {
    const randomNumber = Math.floor(100000000 + Math.random() * 900000000);
    return `user${randomNumber}`;
  };

  getSafeUsername = (value) => {
    const cleaned = String(value || "").trim();

    if (!cleaned || !isUsernameAllowed(cleaned)) {
      return this.generateDefaultUsername();
    }

    return cleaned.slice(0, 24);
  };

  saveGuestUsername = () => {
    try {
      localStorage.setItem(
        LS_USERNAME_KEY,
        this.getSafeUsername(this.state.username)
      );
    } catch {
      // ignore
    }
  };

  changeUsername = (nextUsername) => {
    const cleaned = String(nextUsername || "").trim();

    if (!isUsernameAllowed(cleaned)) {
      return false;
    }

    this.setState({
      username: this.getSafeUsername(cleaned),
    });

    return true;
  };

  render() {
    if (this.state.screen === "home") {
      return (
        <>
          {this.renderAdminPanel()}

          <Home
            onPlay={this.goPlay}
            onProfile={this.goProfile}
            onLoginSuccess={(user) => this.setState({ user, isAuthenticated: true })}
            onLogout={this.handleLogout}
            isAuthenticated={this.state.isAuthenticated}
            user={this.state.user}
          />
        </>
      );
    }

    if (this.state.screen === "profile") {
      return (
        <>
          {this.renderAdminPanel()}

          <Profile
            username={this.state.username}
            onChangeUsername={this.changeUsername}
            unlockedIds={this.state.collectionUnlocked}
            selectedProfileImageId={this.state.profileImageId}
            onSelectProfileImage={this.selectProfileImage}
            onCollection={this.goCollection}
            onAchievements={this.goAchievements}
            onBackHome={this.goHome}
            cardsOwned={(this.state.collectionUnlocked || []).length}
            gamesPlayed={this.state.gamesPlayed || 0}
            foilsFound={(this.state.collectionFoilUnlocked || []).length}
          />

          <BottomNav className="bottomNav">
            <div className="appVersion">{APP_VERSION}</div>
          </BottomNav>
        </>
      );
    }

    if (this.state.screen === "collection") {
      return (
        <>
          <AppBar position="fixed" color="secondary">
            <nav className="navbar navbar-default myNavColor">
              <div className="container-fluid">
                <div className="navRow">
                  <div className="navLeft">
                    <h2 className="whiteText impactFont navTitle">
                      Memory Mix-Up
                    </h2>

                    <button className="homeNavButton" onClick={this.goHome}>
                      Home
                    </button>

                    <button className="homeNavButton" onClick={this.goPlay}>
                      Play
                    </button>

                    <button className="homeNavButton" onClick={this.goProfile}>
                      Profile
                    </button>
                  </div>

                  <div className="navRight whiteText">
                    <h4>
                      Collection: {(this.state.collectionUnlocked || []).length}/
                      {characters.length}
                    </h4>
                  </div>
                </div>
              </div>
            </nav>
          </AppBar>

          {this.renderAdminPanel()}

          <Collection
            unlockedIds={this.state.collectionUnlocked}
            foilUnlockedIds={this.state.collectionFoilUnlocked}
            onBackHome={this.goHome}
          />

          <BottomNav className="bottomNav">
            <div className="appVersion">{APP_VERSION}</div>
          </BottomNav>
        </>
      );
    }

    if (this.state.screen === "achievements") {
      const unlockedCount = (this.state.collectionUnlocked || []).length;

      return (
        <>
          <AppBar position="fixed" color="secondary">
            <nav className="navbar navbar-default myNavColor">
              <div className="container-fluid">
                <div className="navRow">
                  <div className="navLeft">
                    <h2 className="whiteText impactFont navTitle">
                      Memory Mix-Up
                    </h2>

                    <button className="homeNavButton" onClick={this.goHome}>
                      Home
                    </button>

                    <button className="homeNavButton" onClick={this.goPlay}>
                      Play
                    </button>

                    <button className="homeNavButton" onClick={this.goProfile}>
                      Profile
                    </button>
                  </div>

                  <div className="navRight whiteText">
                    <h4>
                      Achievements: {Math.floor(unlockedCount / 10)}/10
                    </h4>
                  </div>
                </div>
              </div>
            </nav>
          </AppBar>

          {this.renderAdminPanel()}

          <Achievements
            unlockedCount={unlockedCount}
            totalCards={characters.length}
            onBackHome={this.goHome}
          />

          <BottomNav className="bottomNav">
            <div className="appVersion">{APP_VERSION}</div>
          </BottomNav>
        </>
      );
    }

    if (this.state.screen === "recap") {
      return (
        <Recap
          outcome={this.state.runOutcome}
          levelReached={this.state.runLevelReached}
          correctClicks={this.state.runCorrectClicks}
          foilsFound={this.state.runFoilsFound}
          newlyUnlocked={this.state.runNewUnlocks}
          totalUnlocked={(this.state.collectionUnlocked || []).length}
          totalCards={characters.length}
          runMode={this.state.runMode}
          runEndReason={this.state.runEndReason}
          runElapsedText={formatRunDuration(
            this.state.runFinalElapsedMs || this.state.runElapsedMs
          )}
          onPlayAgain={this.goPlay}
          onMainMenu={this.goHome}
        />
      );
    }

    const totalCards = this.state.cards.length;
    const cardsPerRow = Math.min(4, Math.ceil(Math.sqrt(totalCards)));
    document.documentElement.style.setProperty("--cards-per-row", cardsPerRow);

    return (
      <>
        {this.state.showLevelCompleteOverlay && (
          <div className="levelCompleteOverlay" role="dialog" aria-modal="true">
            <div className="levelCompleteOverlay__panel">
              <div className="levelCompleteOverlay__title">
                Level {this.state.currentLevel + 1} Complete!
              </div>

              {this.isFinalMainLevelCompleteChoice() ? (
                <>
                  <div className="levelCompleteOverlay__message">
                    You completed the main run. Finish now or continue into Endless Mode.
                  </div>

                  <div className="levelCompleteOverlay__actions">
                    <button
                      className="levelCompleteOverlay__button"
                      onClick={this.finishRun}
                    >
                      Finish
                    </button>

                    <button
                      className="levelCompleteOverlay__button"
                      onClick={this.startNextLevel}
                    >
                      Endless
                    </button>
                  </div>
                </>
              ) : (
                <button
                  className="levelCompleteOverlay__button"
                  onClick={this.startNextLevel}
                >
                  {this.state.pendingNextLevel >= MAX_ENDLESS_LEVEL
                    ? "View Recap"
                    : `Start Level ${
                        (this.state.pendingNextLevel ?? this.state.currentLevel + 1) + 1
                      }`}
                </button>
              )}
            </div>
          </div>
        )}

        {this.state.showHeartContinueOverlay && (
          <div className="heartContinueOverlay" role="dialog" aria-modal="true">
            <div className="heartContinueOverlay__panel">
              <div className="heartContinueOverlay__title">Wrong card!</div>

              <div className="heartContinueOverlay__message">
                Spend 1 heart to continue this run?
              </div>

              <div className="heartContinueOverlay__hearts">
                Hearts: {"❤️".repeat(this.state.hearts)}
                {"🤍".repeat(MAX_HEARTS - this.state.hearts)}
              </div>

              <div className="heartContinueOverlay__actions">
                <button
                  type="button"
                  className="levelCompleteOverlay__button"
                  onClick={this.spendHeartToContinue}
                  disabled={this.state.hearts <= 0}
                >
                  Spend Heart
                </button>

                <button
                  type="button"
                  className="levelCompleteOverlay__button"
                  onClick={this.declineHeartContinue}
                >
                  End Run
                </button>
              </div>
            </div>
          </div>
        )}

        {this.state.showLevelOverlay && (
          <div className="levelOverlay" role="status" aria-live="polite">
            <div className="levelOverlay__panel">
              <div className="levelOverlay__label">Level</div>
              <div className="levelOverlay__value">
                {this.state.currentLevel + 1}
              </div>
            </div>
          </div>
        )}

        <AppBar position="fixed" color="secondary">
          <nav className="navbar navbar-default myNavColor">
            <div className="container-fluid">
              <div className="navRow">
                <div className="navLeft">
                  <h2 className="whiteText impactFont navTitle">
                    Memory Mix-Up
                  </h2>

                  <button className="homeNavButton" onClick={this.goHome}>
                    Home
                  </button>

                  <button className="homeNavButton" onClick={this.goProfile}>
                    Profile
                  </button>
                </div>

                {/* <div className="navRight whiteText">
                  <h4>
                    Current Score: {this.state.score} || High Score:{" "}
                    {this.state.topScore}
                    <span className="levelTracker">
                      {" "}
                      || Level: {this.state.currentLevel + 1}
                    </span>
                  </h4>
                </div> */}
              </div>
            </div>
          </nav>
        </AppBar>

        {this.renderAdminPanel()}

        <div id="main-content" className="container">
          <div className="gameContent">
            <div className="gameStats" aria-label="Game stats">
              <span>Level: {this.state.currentLevel + 1}</span>
              <span>Current Score: {this.state.score}</span>
              <span>High Score: {this.state.topScore}</span>

              {this.state.runMode === "blitz" ? (
                <span className="gameStatsTimer gameStatsTimer--blitz">
                  Blitz: {formatRunDuration(this.getBlitzRemainingMs())}
                </span>
              ) : (
                <span className="gameStatsTimer">
                  Time: {formatRunDuration(this.state.runElapsedMs)}
                </span>
              )}

              <span>
                Hearts: {"❤️".repeat(this.state.hearts)}
                {"🤍".repeat(MAX_HEARTS - this.state.hearts)}
              </span>

              {this.state.hearts < MAX_HEARTS && !this.state.hardcoreMode && (
                <span>Next: {this.getNextHeartMinutesText()}</span>
              )}

              {this.state.hardcoreMode && <span>Hardcore</span>}
              {this.state.twistedMode && <span>Twisted Mode</span>}
              {this.state.shyCorgisMode && <span>Shy Corgis</span>}
            </div>

            <div
              key={this.state.currentLevel}
              className={`gameBoard gameBoard--levelEnter ${
                this.state.showLevelCompleteOverlay ||
                this.state.showHeartContinueOverlay
                  ? "gameBoard--backgroundBlur"
                  : ""
              }`}
            >
              {this.state.cards.map((card, index) => (
                <Row
                  {...card}
                  handleClick={this.handleClick}
                  index={index}
                  levelKey={this.state.currentLevel}
                  shyCorgisMode={this.state.shyCorgisMode}
                  requestShyCorgiHide={this.requestShyCorgiHide}
                  releaseShyCorgiHide={this.releaseShyCorgiHide}
                  key={card.id}
                />
              ))}
            </div>
          </div>
        </div>

        <BottomNav className="bottomNav">
          <div className="appVersion">{APP_VERSION}</div>
        </BottomNav>
      </>
    );
  }
}

export default App;